// CreditNoteList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
} from "reactstrap";
import {
  FaPlus,
  FaFilter,
  FaArrowAltCircleLeft,
  FaArrowAltCircleRight,
} from "react-icons/fa";
import {
  BsFillArrowUpSquareFill,
  BsFillArrowDownSquareFill,
} from "react-icons/bs";
import { ImDownload } from "react-icons/im";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import swal from "sweetalert";

/* ===== Your shared utilities/components in ../import ===== */
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import {
  convertDataCSVtoExcel,
  exportDataToExcel,
  exportDataToPDF,
  convertDataCsvToXml,
} from "../import/Downloader";

/* ===== API calls in ../import/ApiEndPoint/... ===== */
import {
  CreditnoteOrderList,
  _PostSave,
} from "../import/ApiEndPoint/ApiCalling";
import { Create_credit_Note } from "../import/ApiEndPoint/Api";

/* ===== Local component for Add form (keep your existing one) ===== */
import AddNote from "./AddNote";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
const STORAGE_KEY = "CreditNoteListshow";
const TEMP_BUCKET = []; // selections for Available/Visible pickers

const prettyDate = (iso = "") => (iso ? String(iso).split("T")[0] : "");

/* Build base columns for simple HTML table */
function makeBaseColumns() {
  return [
    { id: "uid", headerName: "UID", width: 65 },
    {
      id: "totalAmount",
      headerName: "Total Amount",
      width: 140,
      accessor: (row) => row?.totalAmount ?? "",
    },
    {
      id: "createdAt",
      headerName: "Creation Date",
      width: 140,
      accessor: (row) => prettyDate(row?.createdAt),
    },
    {
      id: "partyId.CompanyName",
      headerName: "Company Name",
      width: 220,
      accessor: (row) => row?.partyId?.CompanyName ?? "",
    },
    {
      id: "partyId.contactNumber",
      headerName: "Contact Number",
      width: 140,
      accessor: (row) => row?.partyId?.contactNumber ?? "",
    },
    {
      id: "partyId.ownerName",
      headerName: "Owner Name",
      width: 180,
      accessor: (row) => row?.partyId?.ownerName ?? "",
    },
    {
      id: "partyId.address",
      headerName: "Party Address",
      width: 220,
      accessor: (row) => row?.partyId?.address ?? "",
    },
    {
      id: "partyId.email",
      headerName: "Email",
      width: 220,
      accessor: (row) => row?.partyId?.email ?? "",
    },
  ];
}

/* CSV builder for current rows/columns */
function rowsToCSV(rows, columns, page, pageSize) {
  const safe = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const headers = columns.map((c) => safe(c.headerName)).join(",");

  const body = rows
    .map((row, idx) =>
      columns
        .map((c) => {
          if (c.id === "uid") {
            const n = (page - 1) * pageSize + idx + 1;
            return safe(n);
          }
          if (c.accessor) return safe(c.accessor(row));
          return safe(row?.[c.id] ?? "");
        })
        .join(",")
    )
    .join("\n");

  return `${headers}\n${body}`;
}

/* Simple CSV download (for .csv) */
function downloadCSV(csv, filename = "CreditNoteList.csv") {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
export default function CreditNoteList() {
  // permissions + master UI
  const [permissions, setPermissions] = useState({});
  const [masterShow, setMasterShow] = useState(false);

  // loading + data
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // search + pagination
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);

  // columns modal
  const [colModal, setColModal] = useState(false);
  const [allCols, setAllCols] = useState([]);
  const [visibleCols, setVisibleCols] = useState([]);
  const [focusIndex, setFocusIndex] = useState(-1);

  // download hover
  const [dlOpen, setDlOpen] = useState(false);
  const dlTimer = useRef(null);

  // add credit note modal
  const [addModal, setAddModal] = useState(false);

  /* ---------------- init ---------------- */
  useEffect(() => {
    const perms = CheckPermission("CreditNote") || {};
    setPermissions(perms);

    const user = JSON.parse(localStorage.getItem("userData"));
    if (user?.rolename?.roleName === "MASTER") setMasterShow(true);

    if (user?.database) {
      loadAll(user?._id, user?.database);
    }
  }, []);

  const loadAll = async (id, db) => {
    setLoading(true);
    try {
      const res = await CreditnoteOrderList(db, id);
      const list = (res?.CreditNote || []).reverse();
      setRows(list);

      // columns base + restore visible from localStorage
      const base = makeBaseColumns();
      setAllCols(base);

      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // keep only those still in base (in case schema changed)
          const sanitized = parsed.filter((p) =>
            base.some((b) => b.id === p.id)
          );
          setVisibleCols(sanitized.length ? sanitized : base);
        } catch {
          setVisibleCols(base);
        }
      } else {
        setVisibleCols(base);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SuperAdmin master switch ---------------- */
  const handleParentSubmit = (e) => {
    e.preventDefault();
    const value = JSON.parse(localStorage.getItem("SuperadminIdByMaster")); // "id db"
    if (!value) return;
    const [id, db] = String(value).split(" ");
    loadAll(id, db);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  /* ---------------- search + pagination ---------------- */
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    const cols = visibleCols;

    return rows.filter((r) => {
      for (const c of cols) {
        if (c.id === "uid") continue;
        const val = c.accessor ? c.accessor(r) : r[c.id];
        if (val !== undefined && val !== null) {
          if (String(val).toLowerCase().includes(q)) return true;
        }
      }
      return false;
    });
  }, [rows, search, visibleCols]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  /* ---------------- column picker ---------------- */
  const toggleColModal = () => setColModal((s) => !s);

  const onAvailCheck = (_e, col) => {
    const exists = TEMP_BUCKET.find((x) => x.id === col.id);
    if (exists) {
      const idx = TEMP_BUCKET.findIndex((x) => x.id === col.id);
      if (idx > -1) TEMP_BUCKET.splice(idx, 1);
    } else {
      TEMP_BUCKET.push(col);
    }
  };

  const addSelectedToVisible = () => {
    const merged = [
      ...visibleCols,
      ...TEMP_BUCKET.filter((b) => !visibleCols.some((c) => c.id === b.id)),
    ];
    setVisibleCols(merged);
  };

  const removeSelectedFromVisible = () => {
    setVisibleCols((cols) =>
      cols.filter((c) => !TEMP_BUCKET.some((b) => b.id === c.id))
    );
  };

  const removeOneVisible = (col) => {
    setVisibleCols((cols) => cols.filter((c) => c.id !== col.id));
  };

  const moveUp = () => {
    if (focusIndex < 1) return;
    const next = [...visibleCols];
    const [m] = next.splice(focusIndex, 1);
    next.splice(focusIndex - 1, 0, m);
    setVisibleCols(next);
    setFocusIndex((i) => i - 1);
  };

  const moveDown = () => {
    if (focusIndex < 0 || focusIndex >= visibleCols.length - 1) return;
    const next = [...visibleCols];
    const [m] = next.splice(focusIndex, 1);
    next.splice(focusIndex + 1, 0, m);
    setVisibleCols(next);
    setFocusIndex((i) => i + 1);
  };

  const saveVisible = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols));
    toggleColModal();
  };

  /* ---------------- download menu ---------------- */
  const onDownloadHover = (e) => {
    if (e.type === "mouseenter") {
      clearTimeout(dlTimer.current);
      setDlOpen(true);
    } else if (e.type === "mouseleave") {
      dlTimer.current = setTimeout(() => setDlOpen(false), 180);
    }
  };

  /* ---------------- exports ---------------- */
  const buildCSV = () => rowsToCSV(filteredRows, visibleCols, page, pageSize);

  const exportPDF = async () => {
    const csv = buildCSV();
    await exportDataToPDF(csv, "CreditNoteList");
  };
  const exportCSV = () => {
    const csv = buildCSV();
    downloadCSV(csv);
  };
  const exportXLS = async () => {
    const csv = buildCSV();
    await convertDataCSVtoExcel(csv, "CreditNoteList");
  };
  const exportXLSX = async () => {
    const csv = buildCSV();
    await exportDataToExcel(csv, "CreditNoteList");
  };
  const exportXML = async () => {
    const csv = buildCSV();
    await convertDataCsvToXml(csv, "CreditNoteList");
  };

  /* ---------------- add credit note ---------------- */
  const toggleAddModal = () => setAddModal((s) => !s);
  const handleAddSubmit = async (e, data) => {
    e.preventDefault();
    try {
      await _PostSave(Create_credit_Note, data);
      const user = JSON.parse(localStorage.getItem("userData"));
      await loadAll(user?._id, user?.database);
      toggleAddModal();
      swal("Success", "Added Successfully", "success");
    } catch (err) {
      swal("Error", "Error Occurred", "error");
    }
  };

  /* ---------------- render ---------------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .page-wrap { padding: 8px; }

        .card-slim { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; }
        .title { font-weight: 600; text-transform: uppercase; font-size: 16px; margin: 6px 0 10px; }
        .muted { color: #64748b; font-size: 12px; }
        .btn-compact { height: 30px; padding: 0 12px; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
        .click { cursor: pointer; }
        .center { text-align: center; }

        .toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }

        /* download hover menu */
        .download-menu { position: absolute; z-index: 5; border: 1px solid rgb(8,91,245); background: #fff; }
        .download-item { cursor: pointer; padding: 6px 10px; font-size: 13px; }
        .download-item:hover { background: #f1f5f9; }

        /* Table compact */
        .table { width: 100%; border-collapse: collapse; }
        .table th, .table td { padding: 8px; border: 1px solid #e5e7eb; font-size: 13px; text-align: center; white-space: nowrap; }
        .table th { background: #f8fafc; font-weight: 600; }

        /* Column picker widgets */
        .picker-panel { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px; max-height: 60vh; overflow: auto; }
        .chip { border: 1px solid #e5e7eb; padding: 6px 10px; border-radius: 999px; display: inline-flex; gap: 8px; align-items: center; background: #f8fafc; }
        .chip.active { background: #e0ecff; }
      `}</style>

      <div className="page-wrap">
        <a href="/#/dashboard" style={{ color: "green", paddingLeft: 10 }}>
          Home
        </a>
        &nbsp;/&nbsp;Lead
        <Card className="card-slim" style={{ marginTop: 8 }}>
          <Row style={{ marginRight: 3, marginLeft: 3, alignItems: "center" }}>
            <Col>
              <h2 className="title">Credit Note List</h2>
            </Col>

            {masterShow ? (
              <Col lg="3" md="4" sm="12" style={{ marginTop: 12 }}>
                <SuperAdminUI
                  onDropdownChange={handleDropdownChange}
                  onSubmit={handleParentSubmit}
                />
              </Col>
            ) : (
              <Col />
            )}

            <Col lg="3" md="4" sm="12" style={{ marginTop: 12 }}>
              <Input
                placeholder="search Item here..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ height: 32, fontSize: 13 }}
              />
            </Col>

            <Col
              lg="1"
              md="2"
              sm="4"
              xs="6"
              className="center"
              style={{ marginTop: 12 }}
            >
              <Button
                className="btn-compact"
                color="primary"
                onClick={toggleAddModal}
                disabled={!permissions?.Create}
                title={
                  !permissions?.Create ? "No permission" : "Add Credit Note"
                }
              >
                <FaPlus size={12} /> Add
              </Button>
            </Col>

            <Col
              lg="1"
              md="2"
              sm="4"
              xs="6"
              className="center"
              style={{ marginTop: 12, position: "relative" }}
            >
              {permissions?.View && (
                <FaFilter
                  className="click"
                  title="Choose columns"
                  size={20}
                  onClick={toggleColModal}
                  color="rgb(8,91,245)"
                />
              )}
              {permissions?.Download && (
                <div
                  onMouseEnter={onDownloadHover}
                  onMouseLeave={onDownloadHover}
                  style={{ display: "inline-block", marginLeft: 10 }}
                >
                  <ImDownload
                    className="click"
                    title="download"
                    size={20}
                    color="rgb(8,91,245)"
                  />
                  {dlOpen && (
                    <div className="download-menu">
                      <div className="download-item" onClick={exportPDF}>
                        .PDF
                      </div>
                      <div className="download-item" onClick={exportCSV}>
                        .CSV
                      </div>
                      <div className="download-item" onClick={exportXLS}>
                        .XLS
                      </div>
                      <div className="download-item" onClick={exportXLSX}>
                        .XLSX
                      </div>
                      <div className="download-item" onClick={exportXML}>
                        .XML
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>

          {/* Table */}
          {permissions?.View && (
            <>
              <div style={{ overflowX: "auto", marginTop: 8 }}>
                <table className="table">
                  <thead>
                    <tr>
                      {visibleCols.map((col) => (
                        <th
                          key={col.id}
                          style={{ width: col.width ? col.width : "auto" }}
                        >
                          {col.headerName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, idx) => (
                      <tr key={row?._id || idx}>
                        {visibleCols.map((col) => (
                          <td key={col.id}>
                            {col.id === "uid"
                              ? (page - 1) * pageSize + idx + 1
                              : col.accessor
                              ? col.accessor(row)
                              : row?.[col.id] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {pageRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={visibleCols.length}
                          className="center"
                          style={{ padding: 12 }}
                        >
                          No data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination toolbar */}
              <div
                className="toolbar"
                style={{ justifyContent: "space-between", marginTop: 8 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Button
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <span style={{ fontSize: 12 }}>
                    Page {page} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12 }}>Rows:</span>
                  <Input
                    type="select"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{ width: 90, height: 32 }}
                  >
                    {[10, 15, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Column Picker Modal */}
      <Modal
        isOpen={colModal}
        toggle={toggleColModal}
        style={{ maxWidth: "1050px" }}
      >
        <ModalHeader toggle={toggleColModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            {/* Available Columns */}
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h4 style={{ fontSize: 14, fontWeight: 700 }}>
                Available Columns
              </h4>
              <div className="picker-panel">
                {allCols.map((col) => (
                  <div key={col.id} className="mt-1">
                    <label style={{ cursor: "pointer", fontSize: 13 }}>
                      <input
                        type="checkbox"
                        className="mr-1"
                        onChange={(e) => onAvailCheck(e, col)}
                      />
                      {col.headerName}
                    </label>
                  </div>
                ))}
              </div>
            </Col>

            {/* Arrows */}
            <Col
              lg="2"
              md="2"
              sm="12"
              xl="2"
              xs="12"
              className="d-flex align-items-center"
            >
              <div className="w-100 center">
                <FaArrowAltCircleRight
                  className="click"
                  size={28}
                  onClick={addSelectedToVisible}
                />
                <div className="mt-2">
                  <FaArrowAltCircleLeft
                    className="click"
                    size={28}
                    onClick={removeSelectedFromVisible}
                  />
                </div>
              </div>
            </Col>

            {/* Visible Columns + Reorder */}
            <Col lg="6" md="6" sm="12" xl="6" xs="12">
              <Row>
                <Col lg="8" md="8" sm="12" xs="12">
                  <h4 style={{ fontSize: 14, fontWeight: 700 }}>
                    Visible Columns
                  </h4>
                  <div className="picker-panel">
                    {visibleCols.map((col, i) => (
                      <div key={col.id} className="mt-1">
                        <div
                          className={`chip click ${
                            focusIndex === i ? "active" : ""
                          }`}
                          onClick={() => setFocusIndex(i)}
                        >
                          <IoMdRemoveCircleOutline
                            className="mr-1 click"
                            size={18}
                            color="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeOneVisible(col);
                            }}
                          />
                          {col.headerName}
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col
                  lg="4"
                  md="4"
                  sm="12"
                  xs="12"
                  className="d-flex align-items-center"
                >
                  <div className="w-100 center">
                    <BsFillArrowUpSquareFill
                      className="click mb-1"
                      size={28}
                      onClick={moveUp}
                    />
                    <div>
                      <BsFillArrowDownSquareFill
                        className="click"
                        size={28}
                        onClick={moveDown}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col>
              <div className="d-flex justify-content-center">
                <Button
                  onClick={saveVisible}
                  color="primary"
                  className="btn-compact"
                >
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* Add Credit Note Modal */}
      <Modal
        isOpen={addModal}
        toggle={toggleAddModal}
        className="modal-dialog modal-lg"
        size="md"
      >
        <ModalHeader toggle={toggleAddModal}>Add Credit Note</ModalHeader>
        <ModalBody>
          {/* Keep using your existing AddNote component */}
          <AddNote type="Credit" onSubmit={handleAddSubmit} />
        </ModalBody>
      </Modal>
    </>
  );
}
